'use client'

import { tw } from 'utils/classnames'
import { CheckIcon } from 'primitives/icons'
import type { SiteDataQuery } from 'codegen/generated/cf'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { useTranslations } from 'next-intl'
import { PAYMENT_ENTRY_POINTS } from 'payments/payments-dialog/analytics'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { StartFreeTrialButton } from 'payments/start-free-trial-button'
import { Typography } from '@repo/design-system-cf/components/typography'

import { BannerContainer } from './container'
import { useUserQuery } from 'auth/hooks'
import { AllAccessYearly } from './all-access-yearly'
import { CountdownSection } from '../all-access-yearly-trial'

interface Props {
  banner: Omit<
    NonNullable<SiteDataQuery['siteData']['allAccessYearlyTrialBanner']>,
    '__typename' | 'type'
  > & {
    type: string
  }
  allAccessYearlyBanner: Omit<
    NonNullable<SiteDataQuery['siteData']['allAccessYearlyBanner']>,
    '__typename' | 'type'
  > & {
    type: string
  }
  className?: string
  showCountdown?: boolean
}

const buttonStyles =
  'bg-[--bannerButtonColor] hover:-[bannerButtonHoverColor] text-[--bannerButtonTextColor] h-[45px] w-full items-center justify-center rounded py-5 px-2 text-[1rem] font-bold uppercase tracking-[0.9px] text-center leading-[18px]'

export function AllAccessYearlyTrial({
  banner,
  allAccessYearlyBanner,
  showCountdown = true,
}: Props) {
  const {
    data: {
      isAnonymous,
      isEligibleForAllAccessFreeTrial,
      isEligibleForAllAccessYearlyUpgrade,
    } = {},
    isLoading: userIsLoading,
  } = useUserQuery(undefined, {
    select: data => ({
      isAnonymous: data?.me?.user?.isTemporary,
      isEligibleForAllAccessFreeTrial:
        data?.me?.user?.subscriptionAccess.allAccess.isEligibleForFreeTrial,
      isEligibleForAllAccessYearlyUpgrade:
        data?.me?.user?.subscriptionAccess.allAccess.isEligibleForYearlyUpgrade,
    }),
  })

  const shouldShowAllAccessYearly =
    !userIsLoading &&
    isAnonymous === false &&
    isEligibleForAllAccessFreeTrial === false &&
    isEligibleForAllAccessYearlyUpgrade === true

  if (shouldShowAllAccessYearly) {
    return <AllAccessYearly banner={allAccessYearlyBanner} />
  }

  return <AllAccessYearlyTrialComponent banner={banner} showCountdown={showCountdown} />
}

function AllAccessYearlyTrialComponent({
  banner,
  showCountdown,
}: {
  banner: Props['banner']
  showCountdown: boolean
}) {
  const t = useTranslations('cf_banners')
  const { features, headline, buttonText } = banner

  function onButtonClick() {
    sendAmplitudeEvent(AmplitudeEvents.CLICKED_BANNER_ABOVE_NAV, {
      banner_above_nav: banner.bannerAboveNav,
      banner_above_nav_state: banner.bannerAboveNavState,
    })
  }

  const backgroundImage: string | null = banner.backgroundImage

  const isDailyGiftsPage =
    typeof window !== 'undefined' && window.location.pathname.startsWith(AppRoutes.DAILY_GIFTS)

  const entryPoint = isDailyGiftsPage
    ? PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV_DAILY_GIFTS
    : PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV

  return (
    <div
      style={{
        '--bannerButtonTextColor': '#FFFFFF',
        '--bannerButtonColor': '#248142',
        '--bannerButtonHoverColor': '#1b6633',
        '--checkIconColor': '#248142',
      }}
    >
      <BannerContainer
        backgroundImage={backgroundImage}
        wrapperClassName="items-start"
        bannerType={banner.type}
      >
        <div className="w-full">
          <div
            className="mb-[10px] text-center text-[12px] leading-7 tracking-[0.3px] text-[#2A1F3F] md:text-left md:text-[16px] [&_.u-bold]:text-[16px] [&_.u-bold]:font-bold md:[&_.u-bold]:text-[24px]"
            dangerouslySetInnerHTML={{ __html: headline }}
          />
          <StartFreeTrialButton
            onClick={onButtonClick}
            className={tw(buttonStyles, 'mb-3 flex w-full md:hidden')}
            label={buttonText}
            entryPoint={entryPoint}
          />
          {features ? (
            <ul className="mt-3.5 hidden md:block">
              {features.map((feat, index) => {
                return (
                  <li
                    key={index}
                    className={tw(
                      'my-1 flex items-center leading-[1.618]',
                      index > 0 && 'hidden md:flex',
                    )}
                  >
                    <CheckIcon className="mr-1.5 text-[--checkIconColor]" />
                    <span dangerouslySetInnerHTML={{ __html: feat }} />
                  </li>
                )
              })}
            </ul>
          ) : null}
        </div>
        <div className={tw('hidden w-[47%] flex-col items-center md:flex')}>
          {showCountdown ? (
            <CountdownSection
              freeTodayText={t('try_free_today')}
              hoursText={t('hours')}
              minutesText={t('mins')}
              secondsText={t('secs')}
            />
          ) : null}
          <StartFreeTrialButton
            onClick={onButtonClick}
            className={tw(buttonStyles, 'hidden md:flex')}
            label={buttonText}
            entryPoint={entryPoint}
          />
          <Typography variant="cf-label-02" className="mt-2 text-semantic-foreground-color-subtle">
            {t('cancel_anytime')}
          </Typography>
        </div>
      </BannerContainer>
    </div>
  )
}
